import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";

import { firebaseApp } from "../../../../api/firebase/firestoreService";
import { logError } from "../../../../utils/customLogger";

/** Metadata stored on the history entry for an attached fax document. */
export interface FaxDocumentUpload {
  /** Storage path the file was uploaded to. */
  attachment_path: string;
  /** Original file name. */
  attachment_name: string;
  /** Tokenised download URL for viewing the file from the history tab. */
  attachment_url: string;
}

/**
 * Builds the order-scoped storage path for an attached fax document.
 *
 * @input identifier - Order identifier the summary belongs to
 * @input fileName - Original file name
 * @return Storage path for the attachment
 */
const buildFaxDocumentPath = (identifier: string, fileName: string): string => {
  const uniquePrefix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}`;
  return `non_epa/fax_documents/${identifier}/${uniquePrefix}_${fileName}`;
};

/**
 * Uploads a fax attachment to Firebase Storage.
 *
 * @input file - File selected in the Attach Document control
 * @input identifier - Order identifier the summary belongs to
 * @return Metadata to persist alongside the history entry
 * @raises Error when the upload fails
 */
export const uploadFaxDocument = async (
  file: File,
  identifier: string,
): Promise<FaxDocumentUpload> => {
  try {
    const storage = getStorage(firebaseApp);
    const snapshot = await uploadBytes(
      ref(storage, buildFaxDocumentPath(identifier, file.name)),
      file,
    );

    return {
      attachment_path: snapshot.ref.fullPath,
      attachment_name: file.name,
      attachment_url: await getDownloadURL(snapshot.ref),
    };
  } catch (error) {
    logError(error as Error, "Error uploading non-EPA fax document");
    throw error;
  }
};
